import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  UsersIcon,
  TrophyIcon,
  CreditCardIcon,
  XMarkIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  FingerPrintIcon,
  FireIcon,
  DocumentPlusIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getAdminInitial = () => {
    const name = user?.name || user?.full_name || user?.email || 'A';
    return name.charAt(0).toUpperCase();
  };

  const navItems = [
    { name: 'Dashboard', icon: HomeIcon, path: '/admin/dashboard' },
    { name: 'War Room', icon: FireIcon, path: '/admin/live-monitor' },
    { name: 'Programs', icon: TrophyIcon, path: '/admin/competitions' },
    { name: 'Quiz (S-1)', icon: DocumentTextIcon, path: '/admin/quiz-manager' },
    { name: 'Assignment (S-2)', icon: DocumentPlusIcon, path: '/admin/assignment-manager' },
    { name: 'Internship Tracker', icon: ChartBarIcon, path: '/admin/internship-tracker' },
    { name: 'InternAssignment', icon: ClipboardDocumentCheckIcon, path: '/admin/internship-weekly-assignment-manager' },
    { name: 'User Management', icon: UsersIcon, path: '/admin/users' },
    { name: 'Certificates', icon: ShieldCheckIcon, path: '/admin/certificates' },
    { name: 'Financials', icon: CreditCardIcon, path: '/admin/payments' },
    { name: 'Settings', icon: Cog6ToothIcon, path: '/admin/settings' },
   
   
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-900 flex text-gray-100">
      
      {/* Sidebar Overlay for Mobile */}
      {!isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setIsSidebarOpen(true)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-gray-800 border-r border-gray-700 
        transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Logo Section */}
        <div className="h-20 flex items-center px-6 border-b border-gray-700 bg-gray-800/50">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow-lg shadow-red-500/20">
              <ShieldCheckIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-white">Admin Hub</h2>
              <p className="text-[10px] uppercase tracking-widest text-red-400 font-semibold">Security Active</p>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto h-[calc(100vh-220px)]">
          {navItems.map((item) => (
            <button
              key={item.name}
              onClick={() => navigate(item.path)}
              className={`
                w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 group
                ${isActive(item.path) 
                  ? 'bg-gradient-to-r from-red-600/20 to-orange-600/10 text-white border border-red-500/20' 
                  : 'text-gray-400 hover:bg-gray-700/50 hover:text-gray-200'}
              `}
            >
              <item.icon className={`h-5 w-5 ${isActive(item.path) ? 'text-red-500' : 'group-hover:text-red-400'}`} />
              <span className="font-medium">{item.name}</span>
            </button>
          ))}
        </nav>

        {/* Improved Admin Profile Section (Bottom of Sidebar) */}
        <div className="absolute bottom-0 w-full p-4 border-t border-gray-700 bg-gray-800">
          <div className="relative" ref={dropdownRef}>
            <button 
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="w-full flex items-center p-2 space-x-3 hover:bg-gray-700/50 rounded-xl transition-all border border-transparent hover:border-gray-600"
            >
              <div className="h-10 w-10 rounded-full bg-red-600/20 border border-red-500/40 flex items-center justify-center text-red-500 font-bold shrink-0">
                {getAdminInitial()}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-bold truncate text-white">
                  {user?.name || user?.full_name || 'Admin User'}
                </p>
                <p className="text-[10px] text-gray-500 truncate lowercase italic">
                  {user?.email}
                </p>
              </div>
            </button>

            {/* Admin Quick-Info Dropdown */}
            <AnimatePresence>
              {isProfileOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: -20, scale: 1 }} // Opens upward
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="absolute bottom-full left-0 w-full mb-2 bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl p-4 z-50 overflow-hidden"
                >
                  <div className="space-y-3">
                    <div className="bg-gray-900/50 p-3 rounded-xl border border-gray-700">
                      <div className="flex items-center gap-2 mb-1">
                        <EnvelopeIcon className="h-3.5 w-3.5 text-red-400" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">System Email</span>
                      </div>
                      <p className="text-xs text-gray-300 truncate">{user?.email}</p>
                    </div>

                    <div className="bg-gray-900/50 p-3 rounded-xl border border-gray-700">
                      <div className="flex items-center gap-2 mb-1">
                        <FingerPrintIcon className="h-3.5 w-3.5 text-orange-400" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Admin ID</span>
                      </div>
                      <p className="text-xs font-mono text-orange-200">ADM-{user?.id?.slice(-6).toUpperCase() || 'ROOT'}</p>
                    </div>

                    <button
                      onClick={signOut}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 text-xs font-bold text-red-400 hover:bg-red-500 hover:text-white rounded-lg border border-red-500/20 transition-all"
                    >
                      <ArrowLeftOnRectangleIcon className="h-4 w-4" />
                      <span>Terminate Session</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-64' : 'ml-0'}`}>
        <main className="p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
             <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Menu Toggle */}
      <button 
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="lg:hidden fixed bottom-6 right-6 p-4 bg-red-600 rounded-full shadow-2xl text-white z-50 active:scale-90 transition-transform"
      >
        {isSidebarOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
      </button>
    </div>
  );
};

export default AdminLayout;